// guards/country.guard.ts
import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, take, switchMap, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectCountries } from '../../store/referentiel/referentiel.selectors';
import { loadCountries } from '../../store/referentiel/referentiel.actions';
import { Country } from '../interfaces/doctor.interface';
import { CountryStorageService } from '../services/country.storage.service';

@Injectable({
  providedIn: 'root',
})
export class CountryGuard implements CanActivate {
  selectedCountry: Country | null = null;

  constructor(
    private router: Router,
    private store: Store,
    private countryStorage: CountryStorageService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const countryCode = route.params['country'];

    //Si pas de code pays, rediriger vers le pays par défaut
    if (!countryCode) {
      return this.redirectToDefaultCountry();
    }
    return this.store.select(selectCountries).pipe(
      take(1),
      switchMap((countries) => {
        // si pas encore chargé  sur le store → on dispatch
        if (!countries || countries.length === 0) {
          //pas encore charger
          this.store.dispatch(loadCountries());
          // on attend que les pays soient chargés
          return this.store.select(selectCountries).pipe(
            filter((c) => !!c && c.length > 0),
            take(1),
            map((countriesLoaded) =>
              this.validateCountryCode(countryCode, countriesLoaded)
            )
          );
        } else {
          //deja charger sur le store
          const isValid = this.validateCountryCode(countryCode, countries);
          return of(isValid);
        }
      })
    );
  }

  private validateCountryCode(
    code: string,
    countries: Country[] | null
  ): boolean {
    if (!countries || countries.length === 0) {
      return this.handleInvalidCountry();
    }

    // Si on est sur le sous-domaine Orange, forcer le Sénégal
    if (this.countryStorage.isCountryChangeDisabled()) {
      // Rediriger vers le Sénégal si on essaie d'accéder à un autre pays
      if (code.toLowerCase() !== 'sn') {
        this.router.navigate(['/sn/home']);
        return false;
      }
    }

    // Vérifier si le code pays existe
    const country = countries.find(
      (c) => c.code?.toLowerCase() === code.toLowerCase()
    );
    if (country) {
      // Sauvegarder le pays sélectionné via CountryStorageService
      // (setCountry gère déjà le cas Orange et force le Sénégal)
      this.countryStorage.setCountry(country);
      return true;
    }

    return this.handleInvalidCountry();
  }

  private redirectToDefaultCountry(): Observable<boolean> {
    // Si on est sur le sous-domaine Orange, forcer la redirection vers le Sénégal
    if (this.countryStorage.isCountryChangeDisabled()) {
      this.router.navigate(['/sn/home']);
      return of(false);
    }

    // Récupérer le pays par défaut depuis localStorage
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          const country = JSON.parse(saved);
          this.router.navigate([`/${country.code.toLowerCase()}/home`]);
          return of(false);
        } catch {
          // Continuer sans pays par défaut
        }
      }
    }

    // Pas de pays par défaut - rediriger vers la page d'accueil sans code pays
    this.router.navigate(['/home']);
    return of(false);
  }

  private handleInvalidCountry(): boolean {
    // Rediriger vers la page d'accueil si le code n'est pas valide
    const defaultCountry = this.getDefaultCountry();
    if (defaultCountry) {
      this.router.navigate([`/${defaultCountry}`]);
    } else {
      this.router.navigate(['/home']);
    }
    return false;
  }

  //todo: optimize

  private getDefaultCountry(): string {
    // Essayer de récupérer depuis localStorage
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          const country = JSON.parse(saved);
          return country.code.toLowerCase();
        } catch {
          // Continuer sans défaut
        }
      }
    }
    // Pas de pays par défaut - rediriger vers la page d'accueil
    return '';
  }
}
